import type {
  Ata,
  AtaDetalhe,
  Chamado,
  ConcessionariaCarteira,
  FiltroCarteira,
  PosVenda,
  Regional,
  ResumoCarteira
} from "./painelConsultor.types";

/*
 * Contrato das rotas `/painel-consultor/*` (acampa-api-lumen,
 * PainelConsultorController). O escopo sai do token: cada rota devolve só o
 * que o usuário logado enxerga (todas as lojas ativas para o diretor, as dos
 * consultores da regional para o gestor, a própria carteira para o
 * consultor). Só a carteira recebe parâmetros, que estreitam esse escopo.
 * Campos em camelCase, como em `/atas-visita/indicadores`, e todo corpo no
 * envelope `{ data }`.
 *
 * O perfil vem do organograma (módulo organograma, `/organograma/acesso`), não
 * do `codNivel`; quem não tem perfil recebe 403 aqui. Gestores são os usuários
 * de `gestor_estados`, e a regional de cada um tem `codRegional` igual ao
 * `codUsuario` dele. Consultor cujas lojas não caem nos estados de nenhum
 * gestor fica na regional 0, "Sem regional". Lá também fica o consultor 0,
 * "Sem consultor", com as lojas ativas sem consultor Átria responsável: só o
 * diretor o recebe.
 */

/** O prefixo `/api` já faz parte do baseURL do ApiService. */
export const ROTAS_API_PAINEL = {
  estrutura: "/painel-consultor/estrutura",
  indicadores: "/painel-consultor/indicadores",
  carteira: "/painel-consultor/carteira",
  loja: (codConcessionaria: number) =>
    `/painel-consultor/lojas/${codConcessionaria}`,
  chamados: "/painel-consultor/chamados",
  atas: "/painel-consultor/atas",
  ata: (codAta: number) => `/painel-consultor/atas/${codAta}`
} as const;

export interface RespostaApi<T> {
  data: T;
}

/** Cadastro do consultor, sem números. */
export interface ConsultorApi {
  codConsultor: number;
  codRegional: number;
  nome: string;
  regiao: string;
}

/** Cadastro da regional, sem números. */
export type RegionalApi = Omit<Regional, "posVenda">;

/** GET /painel-consultor/estrutura */
export interface EstruturaPainelApi {
  dataReferencia: string;
  regionais: RegionalApi[];
  consultores: ConsultorApi[];
}

/** Números de um consultor no mês e no ano (YTD), contra o ano anterior. */
export interface IndicadoresConsultorApi {
  codConsultor: number;
  visitasMes: number;
  visitasMesAnterior: number;
  posVenda: PosVenda;
  carteira: ResumoCarteira;
}

export interface IndicadoresRegionalApi {
  codRegional: number;
  posVenda: PosVenda;
}

/**
 * GET /painel-consultor/indicadores. O pós-venda vem consolidado em cada
 * escopo (consultor, regional e o total do token): o front não recalcula.
 */
export interface IndicadoresPainelApi {
  /** Última carga dos indicadores, YYYY-MM-DDTHH:mm. */
  atualizadoEm: string;
  consultores: IndicadoresConsultorApi[];
  regionais: IndicadoresRegionalApi[];
  total: PosVenda;
}

/**
 * Recorte da tela na carteira (o drill-down): só estreita o escopo do token.
 * Regional ou consultor fora dele responde 404.
 */
export interface RecorteCarteira {
  codRegional?: number;
  codConsultor?: number;
}

/** Parâmetros de GET /painel-consultor/carteira. */
export interface ConsultaCarteira extends RecorteCarteira {
  /** A partir de 1. */
  pagina: number;
  /** 10, 25 ou 50. */
  porPagina: number;
  busca: string;
  filtro: FiltroCarteira;
}

/**
 * GET /painel-consultor/carteira — uma página da carteira, já buscada,
 * filtrada e ordenada pela API (nunca visitada primeiro, depois mais dias sem
 * visita). As contagens dos filtros são do recorte inteiro, sem a busca. Com o
 * Jira indisponível, `chamadosAbertos` vem nulo em todas as lojas.
 */
export interface CarteiraPainelApi {
  itens: ConcessionariaCarteira[];
  total: number;
  pagina: number;
  porPagina: number;
  contagens: Record<FiltroCarteira, number>;
}

/**
 * GET /painel-consultor/lojas/{codConcessionaria} — a ficha de uma loja do
 * escopo; 404 fora dele. Os números da loja (`loja`) contam qualquer pessoa,
 * como na carteira; as listas seguem o recorte de quem vê (o consultor vê só
 * as dele).
 */
export interface FichaLojaApi {
  /** A mesma linha de /carteira. */
  loja: ConcessionariaCarteira;
  /** Pós-venda só da loja, com o corte do YTD dos indicadores. */
  posVenda: PosVenda;
  /**
   * As 10 atas mais recentes da loja e todas as que pedem acompanhamento, da
   * mais nova para a mais antiga, sem a janela de 90 dias do /atas.
   */
  atas: Ata[];
  /** Chamados da loja no recorte do /chamados. Nulo com o Jira indisponível. */
  chamados: Chamado[] | null;
}

/**
 * GET /painel-consultor/chamados — em andamento e concluídos, lidos do Jira
 * (cache de 5 minutos na API). Com o Jira fora do ar ou sem credencial, a rota
 * responde 503.
 */
export type ChamadosPainelApi = Chamado[];

/** GET /painel-consultor/atas — recentes e as que pedem acompanhamento. */
export type AtasPainelApi = Ata[];

/**
 * GET /painel-consultor/atas/{codAta} — a ata completa, com fotos. 404 quando
 * não existe ou é de consultor fora do escopo.
 */
export type AtaDetalheApi = AtaDetalhe;
